import { motion } from 'framer-motion';
import { AgentNodeComponent } from './AgentNode';
import type { AgentExecution, AgentNode as AgentNodeType, AgentConnection } from '../../types';

interface AgentGraphProps {
  execution?: AgentExecution;
  animated?: boolean;
  className?: string;
  compact?: boolean;
}

const defaultNodes: AgentNodeType[] = [
  {
    id: 'auth',
    type: 'auth',
    name: 'AuthAgent',
    status: 'completed',
    latency: 245,
    startTime: new Date(Date.now() - 5000).toISOString(),
    endTime: new Date(Date.now() - 4755).toISOString(),
    output: { authenticated: true, customerId: 'C-12345' },
  },
  {
    id: 'network',
    type: 'network',
    name: 'NetworkAgent',
    status: 'running',
    latency: 1200,
    startTime: new Date(Date.now() - 3000).toISOString(),
  },
  {
    id: 'ticket',
    type: 'ticket',
    name: 'TicketAgent',
    status: 'waiting',
    latency: 0,
  },
  {
    id: 'supervisor',
    type: 'supervisor',
    name: 'SupervisorAgent',
    status: 'waiting',
    latency: 0,
  },
];

const defaultConnections: AgentConnection[] = [
  { from: 'auth', to: 'network', status: 'completed' },
  { from: 'network', to: 'ticket', status: 'running' },
  { from: 'ticket', to: 'supervisor', status: 'waiting' },
];

export function AgentGraph({
  execution,
  animated = true,
  className,
  compact = false,
}: AgentGraphProps) {
  const nodes = execution?.nodes || defaultNodes;
  const connections = execution?.connections || defaultConnections;

  // Layout positions for nodes (horizontal flow)
  const nodePositions = compact
    ? [
        { x: 60, y: 60 },
        { x: 220, y: 60 },
        { x: 380, y: 60 },
        { x: 540, y: 60 },
      ]
    : [
        { x: 80, y: 100 },
        { x: 280, y: 60 },
        { x: 480, y: 100 },
        { x: 680, y: 80 },
      ];

  const svgWidth = compact ? 680 : 900;
  const svgHeight = compact ? 140 : 220;

  const getConnectionPath = (fromIdx: number, toIdx: number): string => {
    const from = nodePositions[fromIdx];
    const to = nodePositions[toIdx];

    if (!from || !to) return '';

    const midX = (from.x + to.x) / 2;
    const cp1x = from.x + (to.x - from.x) * 0.5;
    const cp1y = from.y;
    const cp2x = from.x + (to.x - from.x) * 0.5;
    const cp2y = to.y;

    return `M ${from.x} ${from.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${to.x} ${to.y}`;
  };

  const getNodePosition = (nodeId: string): number => {
    return nodes.findIndex((n) => n.id === nodeId);
  };

  const connectionStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return '#00d084';
      case 'running':
        return '#e60000';
      case 'failed':
        return '#ff3366';
      case 'waiting':
        return 'rgba(255, 255, 255, 0.15)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  };

  return (
    <motion.div
      className={`relative ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* SVG Connections */}
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="w-full absolute inset-0 pointer-events-none"
        style={{ zIndex: 0 }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Gradient for active connections */}
          <linearGradient id="activeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#e60000" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#ff6b35" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#e60000" stopOpacity="0.8" />
          </linearGradient>

          {/* Glow filter */}
          <filter id="connectionGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Arrow marker */}
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="rgba(255,255,255,0.3)" />
          </marker>

          <marker
            id="arrowhead-active"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#e60000" />
          </marker>
        </defs>

        {/* Connection paths */}
        {connections.map((conn, idx) => {
          const fromIdx = getNodePosition(conn.from);
          const toIdx = getNodePosition(conn.to);
          const path = getConnectionPath(fromIdx, toIdx);
          const color = connectionStatusColor(conn.status);
          const isActive = conn.status === 'running' || conn.status === 'completed';

          return (
            <g key={`${conn.from}-${conn.to}`}>
              {/* Background path (subtle) */}
              <path
                d={path}
                fill="none"
                stroke="rgba(255, 255, 255, 0.05)"
                strokeWidth="3"
                strokeLinecap="round"
              />

              {/* Animated path */}
              <motion.path
                d={path}
                fill="none"
                stroke={isActive && animated ? 'url(#activeGradient)' : color}
                strokeWidth={isActive ? '2.5' : '1.5'}
                strokeLinecap="round"
                filter={isActive ? 'url(#connectionGlow)' : undefined}
                markerEnd={
                  conn.status === 'running'
                    ? 'url(#arrowhead-active)'
                    : 'url(#arrowhead)'
                }
                initial={animated ? { pathLength: 0, opacity: 0 } : { pathLength: 1, opacity: 1 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{
                  pathLength: { duration: 0.8, delay: idx * 0.2, ease: 'easeInOut' },
                  opacity: { duration: 0.3, delay: idx * 0.2 },
                }}
              />

              {/* Flowing dot animation for active connections */}
              {conn.status === 'running' && animated && (
                <motion.circle r="4" fill="#e60000" filter="url(#connectionGlow)">
                  <animateMotion
                    dur="1.5s"
                    repeatCount="indefinite"
                    path={path}
                  />
                </motion.circle>
              )}

              {/* Connection label */}
              {conn.label && (
                <text
                  x={(nodePositions[fromIdx]?.x || 0) + (nodePositions[toIdx]?.x || 0) / 2}
                  y={(nodePositions[fromIdx]?.y || 0) - 10}
                  fill="rgba(255, 255, 255, 0.5)"
                  fontSize="10"
                  textAnchor="middle"
                >
                  {conn.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Nodes positioned over SVG */}
      <div className="relative" style={{ zIndex: 1 }}>
        {nodes.map((node, idx) => {
          const pos = nodePositions[idx];
          if (!pos) return null;

          return (
            <div
              key={node.id}
              className="absolute"
              style={{
                left: `${(pos.x / svgWidth) * 100}%`,
                top: `${(pos.y / svgHeight) * 100}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <AgentNodeComponent
                node={node}
                delay={idx * 0.15}
              />
            </div>
          );
        })}
      </div>

      {/* Status summary */}
      {execution && (
        <div className="absolute bottom-2 right-2 flex items-center gap-3 text-[10px] text-text-muted">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-success" />
            {nodes.filter((n) => n.status === 'completed').length} Done
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-vokentra" />
            {nodes.filter((n) => n.status === 'running').length} Active
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-text-muted" />
            {nodes.filter((n) => n.status === 'waiting').length} Pending
          </span>
        </div>
      )}
    </motion.div>
  );
}

// Mini version for dashboard cards
interface AgentGraphMiniProps {
  nodeCount?: number;
  activeCount?: number;
  className?: string;
}

export function AgentGraphMini({ nodeCount = 4, activeCount = 1, className }: AgentGraphMiniProps) {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {Array.from({ length: nodeCount }).map((_, i) => (
        <div key={i} className="flex items-center">
          <motion.div
            className={`w-2.5 h-2.5 rounded-full ${
              i < activeCount ? 'bg-vokentra' : 'bg-white/20'
            }`}
            animate={
              i === activeCount - 1
                ? {
                    boxShadow: [
                      '0 0 0 0 rgba(230, 0, 0, 0.4)',
                      '0 0 0 6px rgba(230, 0, 0, 0)',
                    ],
                  }
                : undefined
            }
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          {i < nodeCount - 1 && (
            <div className="w-4 h-[2px] bg-white/10 mx-0.5">
              <motion.div
                className="h-full bg-vokentra"
                initial={{ width: '0%' }}
                animate={{ width: i < activeCount - 1 ? '100%' : '0%' }}
                transition={{ duration: 0.5 }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
